import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const works = [
  {
    slug: "nara-therapy",
    title: "Nara Therapy",
    role: "Product Designer",
    description:
      "A therapy and self-care app that helps users maintain peace of mind, alongside a therapist dashboard for managing appointments, patients, and daily activities.",
    image:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/5uoe60nz_Frame%20811636.jpg",
    accent: "bg-emerald-500",
  },
  {
    slug: "fontalk",
    title: "Fontalk",
    role: "Product Designer",
    description:
      "A phone service that helps you build credit through daily actions — your trust grows into real opportunities.",
    image:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/0qakroaq_Frame%20811636%20%281%29.jpg",
    accent: "bg-brand-500",
  },
  {
    slug: "ai-job-finder",
    title: "AI Job Finder",
    role: "Product Designer",
    description:
      "An end-to-end job-hunt agent that finds, applies, and tracks roles for you — turning 11 hours a week into 2.",
    image:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/9hg6pyai_AI.jpg",
    accent: "bg-brand-500",
  },
  {
    slug: "prompt-earn",
    title: "Prompt Earn",
    role: "Product Designer",
    description:
      "We give you valuable digital and physical products and make it easy for you to get paid promoting them from anywhere in the world.",
    image:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/478h9yxf_Frame%20811636.png",
    accent: "bg-lime-400",
  },
  {
    slug: "qaya-technologies",
    title: "Qaya Technologies",
    role: "Product Manager",
    description:
      "Optimize your supermarket checkout experience with our suite of solutions.",
    image:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/3qjp6bp3_Frame%202147225854%20%282%29.png",
    accent: "bg-yellow-400",
  },
];

export default function FeaturedWorks() {
  return (
    <section
      id="projects"
      data-testid="featured-works-section"
      className="bg-white py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between flex-wrap gap-4 mb-16 sm:mb-20"
        >
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950">
            Featured Works
          </h2>
          <span className="text-xs uppercase tracking-widest text-zinc-500">
            Selected Projects · 2020 — 2025
          </span>
        </motion.div>

        <div className="space-y-16 sm:space-y-20">
          {works.map((w, i) => (
            <WorkCard key={w.title} work={w} index={i} />
          ))}
        </div>

        <div className="mt-20 sm:mt-24 flex justify-center">
          <a
            href="#projects"
            data-testid="see-more-projects-btn"
            className="inline-flex items-center gap-2 bg-zinc-950 text-white hover:bg-brand-600 transition-colors text-sm font-medium px-6 py-3 rounded-full"
          >
            See more Projects
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function WorkCard({ work, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.05 }}
      data-testid={`work-card-${index}`}
      className="group"
    >
      <Link
        to={`/case-studies/${work.slug}`}
        data-testid={`work-link-${index}`}
        className="block"
      >
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 group-hover:text-brand-600 transition-colors">
              {work.title}{" "}
              <span className="text-zinc-400 font-normal">— {work.role}</span>
            </h3>
            <p className="mt-3 text-zinc-600 leading-relaxed">
              {work.description}
            </p>
          </div>
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 text-zinc-900 group-hover:bg-zinc-950 group-hover:text-white group-hover:border-zinc-950 transition-colors">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>

        <div className="relative">
          <img
            src={work.image}
            alt={work.title}
            loading="lazy"
            className="w-full h-auto rounded-3xl transition-transform duration-700 group-hover:scale-[1.01]"
          />
          <div className="absolute top-6 left-6">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-white/90 text-zinc-900 backdrop-blur-sm">
              <span className={`h-1.5 w-1.5 rounded-full ${work.accent}`}></span>
              Case Study
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
